import { apiClient } from './apiClient';
import { 
  CreateSiteRequest, 
  Site, 
  UpdateSiteRequest, 
  User
} from '../types';

// Mock sites for development
let mockSites: Site[] = [
  {
    id: '11111111-1111-1111-1111-111111111111',
    name: 'Headquarters',
    description: 'Main headquarters building',
    address: '123 Main St',
    city: 'San Francisco',
    state: 'CA',
    zipCode: '94105',
    latitude: 37.7749,
    longitude: -122.4194,
    geofenceRadius: 100,
    clientId: 'client_1',
    clientName: 'McDonalds',
    companyId: '11111111-1111-1111-1111-111111111111' // SecureGuard company
  },
  {
    id: '22222222-2222-2222-2222-222222222222',
    name: 'Downtown Office',
    description: 'Downtown branch office',
    address: '456 Market St',
    city: 'San Francisco',
    state: 'CA',
    zipCode: '94105',
    latitude: 37.7899,
    longitude: -122.4009,
    geofenceRadius: 150,
    clientId: 'client_1',
    clientName: 'McDonalds',
    companyId: '11111111-1111-1111-1111-111111111111' // SecureGuard company
  },
  {
    id: '33333333-3333-3333-3333-333333333333',
    name: 'Warehouse',
    description: 'Storage and distribution center',
    address: '789 Industrial Blvd',
    city: 'Oakland',
    state: 'CA',
    zipCode: '94621',
    latitude: 37.7575,
    longitude: -122.2048,
    geofenceRadius: 200,
    clientId: 'client_2',
    clientName: 'Walmart',
    companyId: '11111111-1111-1111-1111-111111111111' // SecureGuard company
  },
  {
    id: '44444444-4444-4444-4444-444444444444',
    name: 'Safety First HQ',
    description: 'Safety First main headquarters',
    address: '101 Safety Ave',
    city: 'Los Angeles',
    state: 'CA',
    zipCode: '90001',
    latitude: 34.0522,
    longitude: -118.2437,
    geofenceRadius: 120,
    clientId: 'client_2',
    clientName: 'Walmart',
    companyId: '22222222-2222-2222-2222-222222222222' // SafetyFirst company
  },
  {
    id: '55555555-5555-5555-5555-555555555555',
    name: 'Safety First Branch',
    description: 'Safety First branch office',
    address: '202 Security Blvd',
    city: 'San Diego',
    state: 'CA',
    zipCode: '92101',
    latitude: 32.7157,
    longitude: -117.1611,
    geofenceRadius: 180,
    clientId: 'client_3',
    clientName: 'Target',
    companyId: '22222222-2222-2222-2222-222222222222' // SafetyFirst company
  }
];

/**
 * Service for site management
 */
export class SitesService {
  private inflightRequests = new Map<string, Promise<any>>();
  private sitesCache: Site[] | null = null;
  private sitesCacheTime = 0;
  private static CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  private deduplicatedGet<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const existing = this.inflightRequests.get(key);
    if (existing) {
      return existing as Promise<T>;
    }
    const promise = fetcher().finally(() => {
      this.inflightRequests.delete(key);
    });
    this.inflightRequests.set(key, promise);
    return promise;
  }

  /** Invalidate the in-memory sites cache (called after create/update/delete) */
  invalidateCache(): void {
    this.sitesCache = null;
    this.sitesCacheTime = 0;
  }

  /**
   * Get all sites for the company.
   * Returns a cached result if the cache is less than 5 minutes old.
   */
  async getSites(): Promise<Site[]> {
    if (this.sitesCache && Date.now() - this.sitesCacheTime < SitesService.CACHE_TTL_MS) {
      return this.sitesCache;
    }
    return this.deduplicatedGet('getSites', async () => {
      const sites = await this._getSitesImpl();
      this.sitesCache = sites;
      this.sitesCacheTime = Date.now();
      return sites;
    });
  }

  private async _getSitesImpl(): Promise<Site[]> {
    try {
      // Try to make a real API call first
      const response = await apiClient.get<Site[]>('/sites');
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.log('Using mock sites data due to API error:', error);
      
      // Get the current user's company ID from localStorage
      const currentUserJson = localStorage.getItem('user');
      if (!currentUserJson) {
        console.error('No current user found');
        return [];
      }
      
      const currentUser = JSON.parse(currentUserJson) as User;
      
      // Filter sites by company ID
      const companySites = mockSites.filter(site => site.companyId === currentUser.companyId);
      return [...companySites];
    }
    
    // Fallback to mock data - but still filter by company ID
    const currentUserJson = localStorage.getItem('user');
    if (!currentUserJson) {
      console.error('No current user found');
      return [];
    }
    
    const currentUser = JSON.parse(currentUserJson) as User;
    const companySites = mockSites.filter(site => site.companyId === currentUser.companyId);
    return [...companySites];
  }

  /**
   * Get site by ID
   */
  async getSiteById(id: string): Promise<Site> {
    try {
      // Try to make a real API call first
      const response = await apiClient.get<Site>(`/sites/${id}`);
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.log('Using mock site data due to API error:', error);
      
      // Get the current user's company ID
      const currentUserJson = localStorage.getItem('user');
      if (!currentUserJson) {
        console.error('No current user found');
        throw new Error('Authentication required');
      }
      
      const currentUser = JSON.parse(currentUserJson) as User;
      
      // Find the site in mock data and ensure it belongs to the user's company
      const site = mockSites.find(s => s.id === id && s.companyId === currentUser.companyId);
      
      if (site) {
        return { ...site };
      }
      
      throw new Error('Site not found');
    }
    
    // Fallback to mock data
    const currentUserJson = localStorage.getItem('user');
    if (!currentUserJson) {
      console.error('No current user found');
      throw new Error('Authentication required');
    }
    
    const currentUser = JSON.parse(currentUserJson) as User;
    
    // Find the site in mock data and ensure it belongs to the user's company
    const site = mockSites.find(s => s.id === id && s.companyId === currentUser.companyId);
    
    if (site) {
      return { ...site };
    }
    
    throw new Error('Site not found');
  }

  /**
   * Create a new site
   */
  async createSite(siteData: CreateSiteRequest): Promise<Site> {
    try {
      // Try to make a real API call first
      const response = await apiClient.post<Site>('/sites', siteData);

      if (response.success && response.data) {
        this.invalidateCache();
        return response.data;
      }
    } catch (error) {
      console.log('Using mock site creation due to API error:', error);
      
      // Get the current user's company ID from localStorage
      const currentUserJson = localStorage.getItem('user');
      if (!currentUserJson) {
        console.error('No current user found');
        throw new Error('Authentication required');
      }
      
      const currentUser = JSON.parse(currentUserJson) as User;
      
      // Import clientsService dynamically to avoid circular dependency
      const { clientsService } = await import('./clientsService');
      
      // Find the client to get its name
      let clientName = 'Unknown Client';
      try {
        if (siteData.clientId) {
          const client = await clientsService.getClientById(siteData.clientId);
          clientName = client.name;
        }
      } catch (error) {
        console.error('Error getting client details:', error);
      }

      // Create a new site with a generated ID and the user's company ID
      const newSite: Site = {
        id: `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: siteData.name,
        description: siteData.description || '',
        address: siteData.address,
        city: siteData.city || '',
        state: siteData.state || '',
        zipCode: siteData.zipCode || '',
        latitude: siteData.latitude,
        longitude: siteData.longitude,
        geofenceRadius: siteData.geofenceRadius || 100,
        clientId: siteData.clientId,
        clientName: clientName,
        companyId: currentUser.companyId,
        emergencyContacts: siteData.emergencyContacts || []
      };
      
      // Add to mock sites
      mockSites.push(newSite);
      
    // Note: Default "Check In" and "Check Out" tasks are created by the backend
    // We don't need to create them here as they will be created by the API
    console.log('Default Check In and Check Out tasks will be created by the backend');
      
      return { ...newSite };
    }
    
    // Fallback to mock creation
    const currentUserJson = localStorage.getItem('user');
    if (!currentUserJson) {
      console.error('No current user found');
      throw new Error('Authentication required');
    }
    
    const currentUser = JSON.parse(currentUserJson) as User;
    
    // Import clientsService dynamically to avoid circular dependency
    const { clientsService } = await import('./clientsService');
    
    // Find the client to get its name
    let clientName = 'Unknown Client';
    try {
      if (siteData.clientId) {
        const client = await clientsService.getClientById(siteData.clientId);
        clientName = client.name;
      }
    } catch (error) {
      console.error('Error getting client details:', error);
    }
    
    // Create a new site with a generated ID and the user's company ID
    const newSite: Site = {
      id: `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: siteData.name,
      description: siteData.description || '',
      address: siteData.address,
      city: siteData.city || '',
      state: siteData.state || '',
      zipCode: siteData.zipCode || '',
      latitude: siteData.latitude,
      longitude: siteData.longitude,
      geofenceRadius: siteData.geofenceRadius || 100,
      clientId: siteData.clientId,
      clientName: clientName,
      companyId: currentUser.companyId,
      emergencyContacts: siteData.emergencyContacts || []
    };
    
    // Add to mock sites
    mockSites.push(newSite);
    
    // Note: Default "Check In" and "Check Out" tasks are created by the backend
    // We don't need to create them here as they will be created by the API
    console.log('Default Check In and Check Out tasks will be created by the backend (fallback)');
    
    return { ...newSite };
  }

  /**
   * Update a site
   */
  async updateSite(id: string, siteData: UpdateSiteRequest): Promise<boolean> {
    try {
      // Try to make a real API call first
      const response = await apiClient.put<void>(`/sites/${id}`, siteData);

      if (response.success) {
        this.invalidateCache();
        return true;
      }
    } catch (error) {
      console.log('Using mock site update due to API error:', error);
      
      // Get the current user's company ID
      const currentUserJson = localStorage.getItem('user');
      if (!currentUserJson) {
        console.error('No current user found');
        throw new Error('Authentication required');
      }
      
      const currentUser = JSON.parse(currentUserJson) as User;
      
      // Find the site to update and ensure it belongs to the user's company
      const siteIndex = mockSites.findIndex(s => s.id === id && s.companyId === currentUser.companyId);
      
      if (siteIndex === -1) {
        throw new Error('Site not found');
      }
      
      // Update site data
      mockSites[siteIndex] = {
        ...mockSites[siteIndex],
        ...siteData
      };
      
      return true;
    }
    
    // Fallback to mock update
    const currentUserJson = localStorage.getItem('user');
    if (!currentUserJson) {
      console.error('No current user found');
      throw new Error('Authentication required');
    }
    
    const currentUser = JSON.parse(currentUserJson) as User;
    
    // Find the site to update and ensure it belongs to the user's company
    const siteIndex = mockSites.findIndex(s => s.id === id && s.companyId === currentUser.companyId);
    
    if (siteIndex === -1) {
      throw new Error('Site not found');
    }
    
    // Update site data
    mockSites[siteIndex] = {
      ...mockSites[siteIndex],
      ...siteData
    };
    
    return true;
  }

  /**
   * Delete a site
   */
  async deleteSite(id: string, deleteAllReports: boolean = false): Promise<boolean> {
    try {
      console.log('Attempting to delete site with ID:', id);
      
      // Try to make a real API call first
      const response = await apiClient.delete<void>(`/sites/${id}`, {
        data: { deleteAllReports }
      });
      console.log('API response for delete site:', response);
      
      // Check if the API call was successful
      if (response.success) {
        console.log('Site deleted successfully via API');
        this.invalidateCache();
        return true;
      } else {
        // If the API call was not successful, check for an error message
        console.error('API call returned success: false');
        if (response.message) {
          console.error('API error message:', response.message);
          throw new Error(response.message);
        } else {
          throw new Error('Unexpected API response');
        }
      }
    } catch (error: any) {
      console.log('Error deleting site:', error);
      console.log('Error type:', typeof error);
      
      // If the error already has a message (e.g., from our throw above), just re-throw it
      if (error.message && error.message !== 'Unexpected API response' && !error.message.includes('Network Error')) {
        console.error('Throwing error with message:', error.message);
        throw error;
      }
      
      // Check if the error is from the API and has a response object
      if (error.response && error.response.data && error.response.data.message) {
        console.log('Found API error message in response:', error.response.data.message);
        throw new Error(error.response.data.message);
      }
      
      // If we're in development mode or the API is not available, use mock data
      if (process.env.NODE_ENV === 'development' || (error.message && error.message.includes('Network Error'))) {
        console.log('Falling back to mock data deletion');
        // Get the current user's company ID
        const currentUserJson = localStorage.getItem('user');
        if (!currentUserJson) {
          console.error('No current user found');
          throw new Error('Authentication required');
        }
        
        const currentUser = JSON.parse(currentUserJson) as User;
        
        // Find the site to delete and ensure it belongs to the user's company
        const siteIndex = mockSites.findIndex(s => s.id === id && s.companyId === currentUser.companyId);
        
        if (siteIndex === -1) {
          console.error('Site not found in mock data');
          throw new Error('Site not found');
        }
        
        // Remove from mock sites
        mockSites.splice(siteIndex, 1);
        console.log('Site deleted from mock data');
        
        return true;
      }
      
      // If we get here, it's a real error from the API that doesn't have a message
      console.error('Unhandled API error:', error);
      throw error;
    }
  }
}

// Create a default instance
export const sitesService = new SitesService();

export default sitesService;
